import * as React from 'react';
import { cn } from '@/lib/utils';
import { Label } from '../label';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';

const inputVariants = cva(
  'flex w-full border focus-visible:outline-hidden focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed focus:border-warp-green-500 disabled:border-light-grey-400 disabled:bg-light-grey-200 disabled:text-dark-grey-50 border-light-grey-500 hover:border-dark-grey-300 bg-white text-black rounded-lg',
  {
    variants: {
      size: {
        large: 'h-14 px-4 py-2 pb-0 text-base',
        small: 'h-12 px-4 py-2 pb-0 text-sm',
        responsive: 'h-14 px-4 py-2 pb-0 text-base lg:h-12 lg:text-sm',
      },
    },
    defaultVariants: {
      size: 'large',
    },
  }
);

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  size?: 'large' | 'small'; // Override the native size attribute from Button HTML
  hasError?: boolean;
  hasSuccess?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size = 'large',
      hasError = false,
      hasSuccess = false,
      ...props
    },
    ref
  ) => {
    return (
      <input
        type={type}
        className={cn(
          inputVariants({ size, className }),
          hasSuccess && 'border-grey-300',
          hasError && 'border-status-danger'
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

const FloatingInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, hasError, hasSuccess, ...props }, ref) => {
    return (
      <Input
        placeholder=" "
        hasError={hasError}
        hasSuccess={hasSuccess}
        className={cn('peer', className)}
        ref={ref}
        {...props}
      />
    );
  }
);
FloatingInput.displayName = 'FloatingInput';

const labelVariants = cva(
  'absolute top-4 left-4 z-10 origin-[0] -translate-y-4 scale-75 transform text-dark-grey-100 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-4 peer-focus:-translate-y-4 peer-focus:scale-75 cursor-text peer-focus:text-warp-green-500',
  {
    variants: {
      size: {
        large: 'text-base',
        small: 'text-sm',
        responsive: 'text-base lg:text-sm',
      },
    },
    defaultVariants: {
      size: 'large',
    },
  }
);

const FloatingLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label> &
    VariantProps<typeof labelVariants> & {
      hasError?: boolean;
      hasSuccess?: boolean;
    }
>(({ className, size = 'large', hasError, hasSuccess, ...props }, ref) => {
  return (
    <Label
      className={cn(
        labelVariants({ size, className }),
        hasSuccess && 'peer-focus:text-warp-green-500',
        hasError && 'peer-focus:text-warp-green-500'
      )}
      ref={ref}
      {...props}
    />
  );
});
FloatingLabel.displayName = 'FloatingLabel';

type FloatingLabelInputProps = InputProps & {
  label?: string;
  hasError?: boolean;
  hasSuccess?: boolean;
};

const FloatingLabelInput = React.forwardRef<
  React.ElementRef<typeof FloatingInput>,
  React.PropsWithoutRef<FloatingLabelInputProps>
>(({ id, label, size, hasError, hasSuccess, ...props }, ref) => {
  if (hasError && hasSuccess) {
    console.warn(
      `Input "${label || id}" has both error and success states. Success state will be prioritized.`
    );
  }
  // Override hasError if hasSuccess is true
  const finalProps = {
    ...props,
    hasError: hasSuccess ? false : hasError,
    hasSuccess,
  };
  return (
    <div className="relative flex items-center">
      <FloatingInput
        size={size}
        ref={ref}
        id={id || props.name}
        {...finalProps}
      />
      <span className="absolute right-2">
        {hasSuccess && (
          <CheckCircleIcon
            data-testid="success-icon"
            className="size-6 text-status-success"
          />
        )}
        {!hasSuccess && hasError && (
          <ExclamationCircleIcon
            data-testid="error-icon"
            className="size-6 text-status-danger"
          />
        )}
      </span>
      <FloatingLabel
        size={size}
        hasError={finalProps.hasError}
        hasSuccess={finalProps.hasSuccess}
        htmlFor={id || props.name}
      >
        {label}
      </FloatingLabel>
    </div>
  );
});
FloatingLabelInput.displayName = 'FloatingLabelInput';

export { FloatingLabelInput };
